/**
 * User Management Service
 * Part of TASK-334: Frontend Admin Panel
 *
 * Handles user CRUD operations, invitations, and status changes
 * for the admin panel.
 */

import { httpClient } from '../../lib/httpClient';
import type {
  AdminUser,
  UserListResponse,
  UserCreateData,
  UserUpdateData,
  UserRoleUpdateData,
  UserStatusUpdateData,
  InviteUserData,
  UserRole,
  ClientAssignment,
  UserClientsResponse,
  ClientAssignmentType,
} from '../../types/admin';

class UserManagementService {
  private readonly basePath = '/api/admin/users';

  /**
   * List all users with pagination and filtering
   */
  async listUsers(params?: {
    search?: string;
    role?: UserRole;
    is_active?: boolean;
    page?: number;
    page_size?: number;
  }): Promise<UserListResponse> {
    try {
      const data = await httpClient.get<UserListResponse>(this.basePath, { params });
      return data;
    } catch (error) {
      console.error('Error listing users:', error);
      throw error;
    }
  }

  /**
   * Get a single user by ID
   */
  async getUser(userId: string): Promise<AdminUser> {
    try {
      const data = await httpClient.get<AdminUser>(`${this.basePath}/${userId}`);
      return data;
    } catch (error) {
      console.error('Error fetching user:', error);
      throw error;
    }
  }

  /**
   * Create a new user
   */
  async createUser(userData: UserCreateData): Promise<AdminUser> {
    try {
      const data = await httpClient.post<AdminUser>(this.basePath, userData);
      return data;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  }

  /**
   * Update user profile information
   */
  async updateUser(userId: string, updates: UserUpdateData): Promise<AdminUser> {
    try {
      const data = await httpClient.put<AdminUser>(`${this.basePath}/${userId}`, updates);
      return data;
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }

  /**
   * Change a user's role
   */
  async changeUserRole(userId: string, roleData: UserRoleUpdateData): Promise<AdminUser> {
    try {
      const data = await httpClient.put<AdminUser>(`${this.basePath}/${userId}/role`, roleData);
      return data;
    } catch (error) {
      console.error('Error changing user role:', error);
      throw error;
    }
  }

  /**
   * Activate or deactivate a user
   */
  async changeUserStatus(userId: string, statusData: UserStatusUpdateData): Promise<AdminUser> {
    try {
      const data = await httpClient.put<AdminUser>(`${this.basePath}/${userId}/status`, statusData);
      return data;
    } catch (error) {
      console.error('Error changing user status:', error);
      throw error;
    }
  }

  /**
   * Delete (soft delete) a user
   */
  async deleteUser(userId: string): Promise<{ message: string }> {
    try {
      const data = await httpClient.delete<{ message: string }>(`${this.basePath}/${userId}`);
      return data;
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  }

  // ==================== Invitation Methods ====================

  /**
   * Send a new invitation by creating a user with send_invitation=true.
   * The backend creates the user and sends an invitation email.
   */
  async inviteUser(inviteData: InviteUserData): Promise<AdminUser> {
    try {
      // Create user with invitation flag - backend handles sending the invite
      const userData: UserCreateData = {
        email: inviteData.email,
        role: inviteData.role,
        send_invitation: true,
      };
      const data = await httpClient.post<AdminUser>(this.basePath, userData);
      return data;
    } catch (error) {
      console.error('Error inviting user:', error);
      throw error;
    }
  }

  /**
   * Resend an invitation to a user
   * @param userId - The user ID (not invitation ID)
   */
  async resendInvitation(userId: string): Promise<{ message: string }> {
    try {
      const data = await httpClient.post<{ message: string }>(
        `${this.basePath}/${userId}/resend-invite`
      );
      return data;
    } catch (error) {
      console.error('Error resending invitation:', error);
      throw error;
    }
  }

  // ==================== Password Methods ====================

  /**
   * Force password reset for a user
   */
  async forcePasswordReset(userId: string): Promise<{ message: string }> {
    try {
      const data = await httpClient.post<{ message: string }>(
        `${this.basePath}/${userId}/reset-password`
      );
      return data;
    } catch (error) {
      console.error('Error forcing password reset:', error);
      throw error;
    }
  }

  /**
   * Reset password for a user (admin action)
   */
  async adminResetPassword(
    userId: string,
    newPassword: string,
    mustChangePassword = true
  ): Promise<{ message: string }> {
    try {
      const data = await httpClient.post<{ message: string }>(
        `${this.basePath}/${userId}/reset-password`,
        { new_password: newPassword, must_change_password: mustChangePassword }
      );
      return data;
    } catch (error) {
      console.error('Error resetting password:', error);
      throw error;
    }
  }

  // ==================== Bulk Operations ====================

  /**
   * Bulk update user status
   */
  async bulkUpdateStatus(
    userIds: string[],
    isActive: boolean
  ): Promise<{ updated: number; failed: string[] }> {
    try {
      const data = await httpClient.post<{ updated: number; failed: string[] }>(
        `${this.basePath}/bulk/status`,
        { user_ids: userIds, is_active: isActive }
      );
      return data;
    } catch (error) {
      console.error('Error bulk updating status:', error);
      throw error;
    }
  }

  /**
   * Bulk change user role
   */
  async bulkChangeRole(
    userIds: string[],
    role: UserRole
  ): Promise<{ updated: number; failed: string[] }> {
    try {
      const data = await httpClient.post<{ updated: number; failed: string[] }>(
        `${this.basePath}/bulk/role`,
        { user_ids: userIds, role }
      );
      return data;
    } catch (error) {
      console.error('Error bulk changing role:', error);
      throw error;
    }
  }

  // ==================== Client Assignment Methods ====================

  /**
   * Get all clients assigned to a user
   */
  async getUserClients(userId: string): Promise<ClientAssignment[]> {
    try {
      const data = await httpClient.get<UserClientsResponse>(
        `${this.basePath}/${userId}/clients`
      );
      return data.clients;
    } catch (error) {
      console.error('Error fetching user clients:', error);
      throw error;
    }
  }

  /**
   * Assign a client to a user
   */
  async assignClientToUser(
    userId: string,
    clientId: string,
    assignmentType: ClientAssignmentType = 'primary'
  ): Promise<void> {
    try {
      await httpClient.post(`/api/admin/clients/${clientId}/assign`, {
        user_id: userId,
        assignment_type: assignmentType,
      });
    } catch (error) {
      console.error('Error assigning client to user:', error);
      throw error;
    }
  }

  /**
   * Remove a client assignment from a user
   */
  async unassignClientFromUser(userId: string, clientId: string): Promise<void> {
    try {
      await httpClient.delete(`/api/admin/clients/${clientId}/assign/${userId}`);
    } catch (error) {
      console.error('Error unassigning client from user:', error);
      throw error;
    }
  }
}

export const userManagementService = new UserManagementService();
